/**
 * MEVIA - IPS by Department Dashboard Component
 * Shows IPS breakdown by department with visual comparison
 * Includes modal for department detail with employee list
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { 
  Building2, 
  Users, 
  AlertTriangle, 
  TrendingUp, 
  TrendingDown,
  ChevronRight,
  ChevronLeft,
  Loader2,
  RefreshCw,
  X,
  Eye,
  FileText
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import logger from '../../utils/logger';

// IPS Level configuration
const IPS_LEVELS = {
  SALUDABLE: { min: 70, max: 100, color: '#22C55E', bgColor: 'bg-green-100', textColor: 'text-green-700' },
  MODERADO: { min: 50, max: 69, color: '#F59E0B', bgColor: 'bg-amber-100', textColor: 'text-amber-700' },
  ALTO_RIESGO: { min: 30, max: 49, color: '#F97316', bgColor: 'bg-orange-100', textColor: 'text-orange-700' },
  CRITICO: { min: 0, max: 29, color: '#EF4444', bgColor: 'bg-red-100', textColor: 'text-red-700' }
};

const LEVEL_LABELS = {
  es: { SALUDABLE: 'Saludable', MODERADO: 'Moderado', ALTO_RIESGO: 'Alto Riesgo', CRITICO: 'Crítico' },
  en: { SALUDABLE: 'Healthy', MODERADO: 'Moderate', ALTO_RIESGO: 'High Risk', CRITICO: 'Critical' }
};

/**
 * Large Donut Chart for department card
 */
const DepartmentDonut = ({ distribution, size = 120 }) => {
  const total = Object.values(distribution || {}).reduce((sum, val) => sum + val, 0);
  if (total === 0) return null;
  
  const levels = ['SALUDABLE', 'MODERADO', 'ALTO_RIESGO', 'CRITICO'];
  const center = size / 2;
  const radius = size / 2 - 8;
  const strokeWidth = 16;
  const innerRadius = radius - strokeWidth / 2;
  
  let currentAngle = -90;
  
  const segments = levels.map(level => {
    const count = distribution[level] || 0;
    const percentage = (count / total) * 100;
    const angle = (percentage / 100) * 360;
    
    if (count === 0) return null;
    
    const startAngle = currentAngle;
    const endAngle = currentAngle + angle;
    currentAngle = endAngle;
    
    const startRad = (startAngle * Math.PI) / 180;
    const endRad = (endAngle * Math.PI) / 180;
    
    const x1 = center + innerRadius * Math.cos(startRad);
    const y1 = center + innerRadius * Math.sin(startRad);
    const x2 = center + innerRadius * Math.cos(endRad);
    const y2 = center + innerRadius * Math.sin(endRad);
    
    const largeArc = angle > 180 ? 1 : 0;
    const pathData = `M ${x1} ${y1} A ${innerRadius} ${innerRadius} 0 ${largeArc} 1 ${x2} ${y2}`;
    
    return { level, pathData, color: IPS_LEVELS[level]?.color || '#9CA3AF' };
  }).filter(Boolean);
  
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={center} cy={center} r={innerRadius} fill="none" stroke="#E5E7EB" strokeWidth={strokeWidth} />
      {segments.map((segment) => (
        <path key={segment.level} d={segment.pathData} fill="none" stroke={segment.color} strokeWidth={strokeWidth} strokeLinecap="round" />
      ))}
      <text x={center} y={center + 6} textAnchor="middle" fontSize="20" fontWeight="bold" fill="#374151">
        {total}
      </text>
    </svg>
  );
};

/**
 * Department Card Component - Larger with better layout
 */
const DepartmentCard = ({ department, onClick }) => {
  const { language } = useLanguage();
  const levelConfig = IPS_LEVELS[department.ips_level] || IPS_LEVELS.MODERADO;
  const levelLabel = LEVEL_LABELS[language]?.[department.ips_level] || department.ips_level;
  
  return (
    <div 
      className="bg-white border-2 rounded-xl p-4 hover:shadow-lg transition-all cursor-pointer group"
      style={{ borderColor: levelConfig.color }}
      onClick={() => onClick?.(department)}
      data-testid={`department-card-${department.departamento}`}
    >
      <div className="flex gap-4">
        {/* Left side - Info */}
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <Building2 className="h-4 w-4 text-gray-400" />
            <h3 className="font-bold text-gray-800 truncate">{department.departamento}</h3>
          </div>
          
          <div className="flex items-baseline gap-2 mb-3">
            <span className="text-3xl font-bold" style={{ color: levelConfig.color }}>
              {department.ips?.toFixed(1)}
            </span>
            <Badge className={`${levelConfig.bgColor} ${levelConfig.textColor} border-0 text-xs`}>
              {levelLabel}
            </Badge>
          </div>
          
          <div className="space-y-1 text-sm text-gray-500">
            <div className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              <span>{department.employee_count} {language === 'es' ? 'empleados' : 'employees'}</span>
            </div>
            {department.at_risk_count > 0 && (
              <div className="flex items-center gap-2 text-red-600">
                <AlertTriangle className="h-4 w-4" />
                <span>{department.at_risk_count} {language === 'es' ? 'en riesgo' : 'at risk'}</span>
              </div>
            )}
          </div>
        </div>
        
        {/* Right side - Donut */}
        {department.distribution && (
          <div className="flex-shrink-0">
            <DepartmentDonut distribution={department.distribution} size={100} />
          </div>
        )}
      </div>
      
      <div className="mt-3 pt-3 border-t flex justify-end">
        <span className="text-sm text-gray-400 group-hover:text-teal-600 flex items-center gap-1 font-medium">
          {language === 'es' ? 'Ver detalle' : 'View detail'}
          <ChevronRight className="h-4 w-4" />
        </span>
      </div>
    </div>
  );
};

/**
 * Department Detail Modal
 */
const DepartmentDetailModal = ({ 
  department, 
  allDepartments,
  onClose, 
  onChangeDepartment,
  onViewEmployeeReport 
}) => {
  const { language } = useLanguage();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  
  const currentIndex = allDepartments.findIndex(d => d.departamento === department?.departamento);
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < allDepartments.length - 1;
  
  useEffect(() => {
    const loadEmployees = async () => {
      if (!department || !user?.empresa_id) return;
      
      setLoading(true);
      try {
        const response = await axios.get(
          `/v2/ips/company/${user.empresa_id}?departamento=${encodeURIComponent(department.departamento)}&language=${language}`
        );
        if (response.data.success) {
          setEmployees(response.data.data.employees || []);
        }
      } catch (err) {
        logger.error('Error loading employees:', err);
      } finally {
        setLoading(false);
      }
    };
    
    loadEmployees();
  }, [department, user?.empresa_id, language]);
  
  if (!department) return null;
  
  const levelConfig = IPS_LEVELS[department.ips_level] || IPS_LEVELS.MODERADO;
  const levelLabel = LEVEL_LABELS[language]?.[department.ips_level] || department.ips_level;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div 
        className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div 
          className="p-5 flex items-center justify-between"
          style={{ backgroundColor: `${levelConfig.color}15` }}
        >
          <div className="flex items-center gap-4">
            {/* Navigation */}
            <div className="flex gap-1">
              <button
                onClick={() => hasPrev && onChangeDepartment(allDepartments[currentIndex - 1])}
                disabled={!hasPrev}
                className={`p-2 rounded-lg ${hasPrev ? 'hover:bg-white/50 text-gray-700' : 'text-gray-300 cursor-not-allowed'}`}
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                onClick={() => hasNext && onChangeDepartment(allDepartments[currentIndex + 1])}
                disabled={!hasNext}
                className={`p-2 rounded-lg ${hasNext ? 'hover:bg-white/50 text-gray-700' : 'text-gray-300 cursor-not-allowed'}`}
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
            
            <div>
              <div className="flex items-center gap-2">
                <Building2 className="h-5 w-5" style={{ color: levelConfig.color }} />
                <h2 className="text-xl font-bold text-gray-800">{department.departamento}</h2>
              </div>
              <p className="text-sm text-gray-500">
                {currentIndex + 1} / {allDepartments.length} {language === 'es' ? 'departamentos' : 'departments'}
              </p>
            </div>
          </div>
          
          <button onClick={onClose} className="p-2 hover:bg-white/50 rounded-lg">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>
        
        {/* Summary */}
        <div className="p-5 border-b flex gap-6">
          <div className="flex-1">
            <p className="text-sm text-gray-500 mb-1">{language === 'es' ? 'IPS del Departamento' : 'Department IPS'}</p>
            <div className="flex items-baseline gap-3">
              <span className="text-4xl font-bold" style={{ color: levelConfig.color }}>
                {department.ips?.toFixed(1)}
              </span>
              <Badge className={`${levelConfig.bgColor} ${levelConfig.textColor} border-0`}>
                {levelLabel}
              </Badge>
            </div>
            <div className="mt-3 flex gap-4 text-sm text-gray-500">
              <span className="flex items-center gap-1">
                <Users className="h-4 w-4" />
                {department.employee_count} {language === 'es' ? 'empleados' : 'employees'}
              </span>
              {department.at_risk_count > 0 && (
                <span className="flex items-center gap-1 text-red-600">
                  <AlertTriangle className="h-4 w-4" />
                  {department.at_risk_count} {language === 'es' ? 'en riesgo' : 'at risk'}
                </span>
              )}
            </div>
          </div>
          
          {department.distribution && (
            <DepartmentDonut distribution={department.distribution} size={100} />
          )}
        </div>
        
        {/* Employee List */}
        <div className="p-5 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 250px)' }}>
          <h3 className="font-semibold text-gray-700 mb-3">
            {language === 'es' ? 'Empleados del Departamento' : 'Department Employees'}
          </h3>
          
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-teal-600" />
            </div>
          ) : employees.length === 0 ? (
            <p className="text-center text-gray-500 py-8">
              {language === 'es' ? 'No hay empleados evaluados' : 'No employees assessed'}
            </p>
          ) : (
            <div className="space-y-2">
              {employees.sort((a, b) => (a.ips || 0) - (b.ips || 0)).map((emp, index) => {
                const empLevelConfig = IPS_LEVELS[emp.ips_level] || IPS_LEVELS.MODERADO;
                const empLevelLabel = LEVEL_LABELS[language]?.[emp.ips_level] || emp.ips_level;
                
                return (
                  <div 
                    key={emp.usuario_id || index}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div 
                        className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-lg"
                        style={{ backgroundColor: empLevelConfig.color }}
                      >
                        {emp.ips?.toFixed(0) || '?'}
                      </div>
                      <div>
                        <p className="font-medium text-gray-800">{emp.nombre}</p>
                        <p className="text-sm text-gray-500">{emp.email}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-3">
                      <Badge 
                        className="border-0"
                        style={{ 
                          backgroundColor: `${empLevelConfig.color}20`,
                          color: empLevelConfig.color
                        }}
                      >
                        {empLevelLabel}
                      </Badge>
                      
                      {onViewEmployeeReport && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onViewEmployeeReport(emp)}
                          className="text-teal-600 hover:bg-teal-50"
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          {language === 'es' ? 'Ver Reporte' : 'View Report'}
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Main IPS by Department Dashboard Component
 */
const IPSDepartmentDashboard = ({ onSelectDepartment, onViewEmployeeReport }) => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  
  const loadDepartments = async () => {
    if (!user?.empresa_id) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`/v2/ips/departments/${user.empresa_id}?language=${language}`);
      if (response.data.success) {
        setDepartments(response.data.data.departments || []);
      }
    } catch (err) {
      logger.error('Error loading department IPS:', err);
      setError(language === 'es' ? 'Error al cargar datos' : 'Error loading data');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    loadDepartments();
  }, [user?.empresa_id, language]);
  
  const handleDepartmentClick = (department) => {
    setSelectedDepartment(department);
    if (onSelectDepartment) {
      onSelectDepartment(department);
    }
  };
  
  // Calculate summary stats
  const totalEmployees = departments.reduce((sum, d) => sum + (d.employee_count || 0), 0);
  const totalAtRisk = departments.reduce((sum, d) => sum + (d.at_risk_count || 0), 0);
  const avgIps = departments.length > 0 
    ? departments.reduce((sum, d) => sum + (d.ips || 0), 0) / departments.length 
    : 0;
  
  // Sort departments by IPS (lowest first - most critical)
  const sortedDepartments = [...departments].sort((a, b) => (a.ips || 0) - (b.ips || 0));
  
  // Find worst and best departments
  const worstDept = sortedDepartments[0];
  const bestDept = sortedDepartments[sortedDepartments.length - 1];
  
  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin mr-2 text-teal-600" />
          <span className="text-gray-500">
            {language === 'es' ? 'Cargando departamentos...' : 'Loading departments...'}
          </span>
        </CardContent>
      </Card>
    );
  }
  
  if (error) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <p className="text-red-500 mb-4">{error}</p>
          <Button variant="outline" onClick={loadDepartments}>
            <RefreshCw className="h-4 w-4 mr-2" />
            {language === 'es' ? 'Reintentar' : 'Retry'}
          </Button>
        </CardContent>
      </Card>
    );
  }
  
  if (departments.length === 0) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-gray-500">
          {language === 'es' 
            ? 'No hay datos de departamentos disponibles' 
            : 'No department data available'}
        </CardContent>
      </Card>
    );
  }
  
  return (
    <>
      <Card data-testid="ips-department-dashboard">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center text-lg">
                <Building2 className="h-5 w-5 mr-2 text-teal-600" />
                {language === 'es' ? 'IPS por Departamento' : 'IPS by Department'}
              </CardTitle>
              <CardDescription>
                {language === 'es' 
                  ? 'Comparativa del Índice de Permanencia Saludable entre departamentos'
                  : 'Healthy Retention Index comparison across departments'}
              </CardDescription>
            </div>
            <Button variant="ghost" size="sm" onClick={loadDepartments}>
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Summary Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <p className="text-xs text-gray-500">{language === 'es' ? 'Departamentos' : 'Departments'}</p>
              <p className="text-xl font-bold text-gray-800">{departments.length}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <p className="text-xs text-gray-500">{language === 'es' ? 'Empleados' : 'Employees'}</p>
              <p className="text-xl font-bold text-gray-800">{totalEmployees}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-3 text-center">
              <p className="text-xs text-gray-500">{language === 'es' ? 'IPS Promedio' : 'Avg IPS'}</p>
              <p className="text-xl font-bold text-teal-600">{avgIps.toFixed(1)}</p>
            </div>
            <div className="bg-red-50 rounded-lg p-3 text-center">
              <p className="text-xs text-red-600">{language === 'es' ? 'En Riesgo' : 'At Risk'}</p>
              <p className="text-xl font-bold text-red-600">{totalAtRisk}</p>
            </div>
          </div>
          
          {/* Highlights - Worst and Best */}
          {departments.length > 1 && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
              {worstDept && worstDept.ips < 60 && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <AlertTriangle className="h-4 w-4 text-red-500" />
                    <span className="text-sm font-medium text-red-700">
                      {language === 'es' ? 'Requiere Atención' : 'Needs Attention'}
                    </span>
                  </div>
                  <p className="text-sm text-red-600">
                    <strong>{worstDept.departamento}</strong> - IPS {worstDept.ips?.toFixed(1)}
                    {worstDept.at_risk_count > 0 && (
                      <span> ({worstDept.at_risk_count} {language === 'es' ? 'en riesgo' : 'at risk'})</span>
                    )}
                  </p>
                </div>
              )}
              {bestDept && bestDept.ips >= 60 && (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div className="flex items-center gap-2 mb-1">
                    <TrendingUp className="h-4 w-4 text-green-500" />
                    <span className="text-sm font-medium text-green-700">
                      {language === 'es' ? 'Mejor Desempeño' : 'Best Performing'}
                    </span>
                  </div>
                  <p className="text-sm text-green-600">
                    <strong>{bestDept.departamento}</strong> - IPS {bestDept.ips?.toFixed(1)}
                  </p>
                </div>
              )}
            </div>
          )}
          
          {/* Department Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {sortedDepartments.map((dept) => (
              <DepartmentCard
                key={dept.departamento}
                department={dept}
                onClick={handleDepartmentClick}
              />
            ))}
          </div>
        </CardContent>
      </Card>
      
      {/* Department Detail Modal */}
      {selectedDepartment && (
        <DepartmentDetailModal
          department={selectedDepartment}
          allDepartments={sortedDepartments}
          onClose={() => setSelectedDepartment(null)}
          onChangeDepartment={setSelectedDepartment}
          onViewEmployeeReport={onViewEmployeeReport}
        />
      )}
    </>
  );
};

export default IPSDepartmentDashboard;
